import React from 'react';
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';
import { Field, Form, FormElement, FormRenderProps } from '@progress/kendo-react-form';
import { FILE_FIELD, NEW_DOCUMENT } from '../../../config/consts';
import { requiredValidator } from './validators/FormValidator';
import { useGetCtsWithCreatePermission } from '../../../context/contentTypes/useGetCtsWithCreatePermission';
import { useGetContentType } from '../../../context/contentTypes/useGetContentType';
import { Button } from '@progress/kendo-react-buttons';
import { FieldMapper } from './FieldMapper';
import { FormUpload } from './fields/FormUpload';
import GearLoader from '../GearLoader';
import { useGlobalizationContext } from '../../../context/globalization/GlobalizationContext';
import { useGetAllFields } from '../../../context/fields/useGetAllFields';
import { useDocumentFormContext } from '../../../context/form/DocumentFormContext';
import { useTranslation } from '../../../context/globalization/useTranslation';
import { useGetMultiFileUploadLimit } from '../../../context/configuration/useGetMultiFileUploadLimit';
import { useFieldInteractions } from './fieldInteractions/useFieldInteractions';
import { useDocumentFormInit } from './hooks/useDocumentFormInit';
import { useDocumentSubmission } from './hooks/useDocumentSubmission';
import { useFileUploadConfig } from './hooks/useFileUploadConfig';
import { useFieldGroups } from './hooks/useFieldGroups';
import { ContentTypeFieldResponse } from '../../../Model/responseDtos/ContentTypeFieldResponse';
import { FormFileMetadataTable } from './fields/metadataGrid/FormFileMetadataTable';
import { useGetCurrentUser } from '../../../context/currentUser/useGetCurrentUser';
import { FormContentTypeField } from './fields/FormContentTypeField';
import { FormFieldsSection } from './fields/FormFieldsSection';
import {
  shouldShowFileMetadataTable,
  shouldShowFileUploadBottom,
  shouldShowFileUploadTop
} from './DocumentFormHelpers';
import { FormRetryCheckbox } from './fields/FormRetryCheckbox';
import { FormErrorList } from './fields/FormErrorList';

const FORM_ID = 'GEAR_DOCUMENT_FORM';

interface DocumentFormContentProps {
  indexId: number;
}

export const DocumentFormContent: React.FC<DocumentFormContentProps> = ({ indexId }) => {
  const { editedDocument, closeDocumentForm, initialFormData, isRetry, fileUploadRequired } = useDocumentFormContext();
  const { data: ctsWithCreatePermissions } = useGetCtsWithCreatePermission(indexId);
  const [attemptedSubmit, setAttemptedSubmit] = React.useState(false);
  const [retry, setRetry] = React.useState(false);
  const { selectedLocale } = useGlobalizationContext();
  const { translate } = useTranslation(selectedLocale);
  const { data: allFields } = useGetAllFields(selectedLocale, indexId);
  const { data: user } = useGetCurrentUser();

  const initCtName =
    editedDocument === NEW_DOCUMENT ? initialFormData?.contentType || undefined : editedDocument?.Cps_ContentType;
  const [selectedCtName, setSelectedCtName] = React.useState<string | undefined>(initCtName);
  const { data: contentType } = useGetContentType(selectedCtName, selectedLocale);
  const { data: multiFileUploadLimit } = useGetMultiFileUploadLimit(indexId);

  const dialogTitle = translate(
    editedDocument === NEW_DOCUMENT
      ? 'custom.toolbar.actions.form.addNewTitle'
      : 'custom.toolbar.actions.form.editTitle'
  );
  const ctFields = React.useMemo(() => contentType?.fields || [], [contentType?.fields]);

  const { allowsMultiFileUpload, perFileFields, maxFiles, needsToRenderFilesInTable } = useFileUploadConfig({
    contentType,
    ctFields,
    multiFileUploadLimit
  });

  const isNewDocument = editedDocument === NEW_DOCUMENT;

  const {
    initialFormValues,
    documentETag,
    formKey,
    isLoading: isInitLoading,
    existingDocumentData: editedDocData
  } = useDocumentFormInit({
    indexId,
    isNewDocument,
    isRetry,
    fileUploadRequired,
    existingDocumentId: editedDocument !== NEW_DOCUMENT ? editedDocument?.Cps_DocId : undefined,
    existingDocumentCtName: editedDocument !== NEW_DOCUMENT ? editedDocument?.Cps_ContentType : undefined,
    prefillData: initialFormData,
    contentTypeFields: ctFields,
    perFileFields,
    selectedContentTypeName: selectedCtName
  });

  const { submitForm } = useDocumentSubmission({
    indexId,
    locale: selectedLocale,
    isNewDocument,
    contentTypeFields: ctFields,
    allowsMultiFileUpload: contentType?.multiFileUpload === true,
    existingDocument: editedDocData?.data.metadata,
    existingDocumentETag: documentETag,
    existingDocumentFileName: editedDocData?.data.metadata?.Cps_FileName as string
  });

  const {
    getFieldChangeHandler,
    getChoiceFilterHandler,
    getManualSearchHandler,
    loadingFields,
    dynamicChoices,
    getFieldChoices,
    handleFileFieldChange,
    isFieldEnabled
  } = useFieldInteractions({
    ctFields,
    selectedCtName
  });

  const { grouped: groupedFields, ungrouped: ungroupedFields } = useFieldGroups({
    fields: ctFields,
    groups: contentType?.groups
  });

  const handleSubmit = async (dataItem: any) => {
    await submitForm(dataItem, closeDocumentForm, retry);
  };

  const renderField = (ctField: ContentTypeFieldResponse, formRenderProps: FormRenderProps) => {
    return (
      <FieldMapper
        key={ctField.name}
        ctField={ctField}
        fieldTranslation={allFields?.find(x => x.name === ctField.name)?.displayName}
        choices={dynamicChoices[ctField.name] || ctField.choices || []}
        isLoading={loadingFields.has(ctField.name)}
        onChange={value => {
          const handler = getFieldChangeHandler(ctField);
          handler(ctField.name, value, formRenderProps);
        }}
        onFilterChange={value => {
          const handler = getChoiceFilterHandler(ctField);
          handler(ctField.name, value, formRenderProps);
        }}
        onSearch={value => {
          const handler = getManualSearchHandler(ctField);
          handler(ctField.name, value, formRenderProps);
        }}
        isEnabled={isFieldEnabled(ctField)}
      />
    );
  };

  const handleContentTypeChange = (contentTypeName: string) => {
    setSelectedCtName(contentTypeName);
  };

  if (isInitLoading) {
    return <GearLoader />;
  }

  return (
    <Dialog title={dialogTitle} onClose={closeDocumentForm} contentStyle={{ minWidth: 500 }} className="gearFormDialog">
      <Form
        key={formKey}
        id={FORM_ID}
        onSubmit={handleSubmit}
        onSubmitClick={() => setAttemptedSubmit(true)}
        initialValues={initialFormValues}
        render={(formRenderProps: FormRenderProps) => {
          return (
            <FormElement noValidate>
              <fieldset className={'k-form-fieldset gearFormWrapper'}>
                <FormContentTypeField
                  ctsWithCreatePermissions={ctsWithCreatePermissions}
                  editedDocument={editedDocument}
                  onContentTypeChange={handleContentTypeChange}
                  formRenderProps={formRenderProps}
                />

                {shouldShowFileUploadTop(contentType) && (
                  <Field
                    name={FILE_FIELD}
                    component={FormUpload}
                    optional={false}
                    editedDocument={editedDocument}
                    validator={requiredValidator}
                    multiple={allowsMultiFileUpload}
                    filesRenderedElsewhere={needsToRenderFilesInTable}
                    onFileSelect={(files: FileList) => {
                      handleFileFieldChange(FILE_FIELD, files, formRenderProps);
                    }}
                    isProcessing={loadingFields.has(FILE_FIELD)}
                    maxFiles={maxFiles}
                  />
                )}

                <FormFieldsSection
                  ungroupedFields={ungroupedFields}
                  groupedFields={groupedFields}
                  renderField={renderField}
                  formRenderProps={formRenderProps}
                />

                {shouldShowFileUploadBottom(contentType) && (
                  <Field
                    name={FILE_FIELD}
                    component={FormUpload}
                    optional={false}
                    editedDocument={editedDocument}
                    validator={requiredValidator}
                    multiple={allowsMultiFileUpload}
                    filesRenderedElsewhere={needsToRenderFilesInTable}
                    maxFiles={maxFiles}
                  />
                )}

                <FormFileMetadataTable
                  show={shouldShowFileMetadataTable(
                    needsToRenderFilesInTable,
                    formRenderProps.valueGetter(FILE_FIELD)?.length ?? 0
                  )}
                  files={(formRenderProps.valueGetter(FILE_FIELD) || []).map((item: any) => {
                    if (item.file) {
                      return item;
                    }
                    return {
                      file: item,
                      perFileMetadata: item.perFileMetadata || {}
                    };
                  })}
                  perFileFields={perFileFields}
                  getFieldChoices={getFieldChoices}
                  onChange={updatedFiles => {
                    formRenderProps.onChange(FILE_FIELD, { value: updatedFiles });
                  }}
                />
              </fieldset>

              <FormRetryCheckbox userEmail={user?.email} retry={retry} onRetryChange={setRetry} />
              <FormErrorList errors={formRenderProps.errors} show={attemptedSubmit} />
            </FormElement>
          );
        }}
      />
      <DialogActionsBar>
        <Button type="button" onClick={closeDocumentForm} size={'small'}>
          {translate('custom.common.cancel')}
        </Button>
        <Button form={FORM_ID} type="submit" disabled={!contentType} size={'small'} themeColor={'primary'}>
          {translate('custom.common.save')}
        </Button>
      </DialogActionsBar>
    </Dialog>
  );
};
